import { APIConfig } from "@/lib/api";
import { ApiError } from "@/lib/api/api-error";
import { request as __request } from "@/lib/api/request";
import { z } from "zod";

import {
  type Product,
  type Warehouse,
  productsSchema,
  warehousesSchema,
} from "./types/products";

const paginatedProductsSchema = z.object({
  count: z.number(),
  next: z.string().nullable(),
  previous: z.string().nullable(),
  results: productsSchema,
});

const inventoryCustomersSchema = z.array(z.object({
  id: z.coerce.string(),
  supplier: z.coerce.string(),
  erp_contact_id: z.coerce.string().optional().default(""),
  contact_name: z.string(),
  company_name: z.string(),
  first_name: z.string(),
  last_name: z.string(),
  email: z.coerce.string().optional().default(""),
  phone: z.string(),
  mobile: z.string(),
  created_at: z.string(),
  updated_at: z.string()
}));

export type PaginatedProducts = z.infer<typeof paginatedProductsSchema>;
export type InventoryCustomers = z.infer<typeof inventoryCustomersSchema>;
export type InventoryWarehouses = Warehouse[];

export class ProductService {
  static async list({
    page = 1,
    pageSize = 100,
    filters = {},
  }: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, string | undefined>;
  } = {}): Promise<PaginatedProducts | null> {
    try {
      const query: Record<string, string> = {
        page: page.toString(),
        page_size: pageSize.toString(),
      };

      for (const [key, value] of Object.entries(filters)) {
        if (typeof value === "string" && value.trim()) {
          query[key] = value.trim();
        }
      }

      const response = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/inventory/products/",
          query,
        },
        z.union([paginatedProductsSchema, productsSchema]),
      );

      if (Array.isArray(response)) {
        const coerced: PaginatedProducts = {
          count: response.length,
          next: null,
          previous: null,
          results: response,
        };
        return coerced;
      }

      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[ProductService.list] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[ProductService.list] Unexpected error:", error);
      }
      return null;
    }
  }

  static async listCustomers({
    page = 1,
    pageSize = 100,
    filters = {},
  }: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, string | undefined>;
  } = {}): Promise<InventoryCustomers | []> {
    try {
      const query: Record<string, string> = {
        page: page.toString(),
        page_size: pageSize.toString(),
      };

      for (const [key, value] of Object.entries(filters)) {
        if (typeof value === "string" && value.trim()) {
          query[key] = value.trim();
        }
      }

      const response = await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/inventory/customers/",
          query,
        },
        inventoryCustomersSchema,
      );

      return response;
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[ProductService.list] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[ProductService.list] Unexpected error:", error);
      }
      return [];
    }
  }

  static async listWarehouses(): Promise<InventoryWarehouses> {
    try {
      return await __request(
        APIConfig,
        {
          method: "GET",
          url: "/api/v1/inventory/warehouses/",
        },
        warehousesSchema,
      );
    } catch (error) {
      if (error instanceof ApiError) {
        console.error(
          `[ProductService.listWarehouses] API Error: ${error.message} (Status: ${error.status})`,
          error.body,
        );
      } else {
        console.error("[ProductService.listWarehouses] Unexpected error:", error);
      }
      return [];
    }
  }

  /**
   * Trigger a new product sync request.
   * Returns the created sync request or info about an existing one.
   */
  static async syncProducts(): Promise<SyncProductsResponse> {
    return __request(
      APIConfig,
      {
        method: "POST",
        url: "/api/v1/inventory/sync-products/",
      },
      syncProductsResponseSchema,
    );
  }

  /**
   * Get sync status by ID or get the latest sync request.
   * @param id - Sync request ID or 'latest' to get the most recent
   */
  static async getSyncStatus(
    id: number | "latest" = "latest",
  ): Promise<ProductSyncRequest | null> {
    try {
      return await __request(
        APIConfig,
        {
          method: "GET",
          url: `/api/v1/inventory/sync-products/${id}/`,
        },
        productSyncRequestSchema,
      );
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        return null;
      }
      throw error;
    }
  }
}

// --- Sync Request Types ---

/**
 * Represents a product sync request record from the database.
 */
export const productSyncRequestSchema = z.object({
  id: z.number(),
  supplier: z.number(),
  triggered_by: z.number(),
  triggered_by_email: z.string(),
  status: z.enum(["pending", "processing", "completed", "failed"]),
  products_created: z.number().nullable(),
  products_updated: z.number().nullable(),
  products_total: z.number().nullable(),
  log: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type ProductSyncRequest = z.infer<typeof productSyncRequestSchema>;

/**
 * Response from triggering a new product sync.
 */
export const syncProductsResponseSchema = z.object({
  status: z.enum(["started", "already_running", "error"]),
  message: z.string(),
  sync_request: productSyncRequestSchema.optional(),
});

export type SyncProductsResponse = z.infer<typeof syncProductsResponseSchema>;
